import { useMemo } from 'react';
import * as React from 'react';

import {
  EmployeeProfileTableStyled,
  fillCardSx,
  GridColDef,
  useNoRowsOverlay,
  useTheme,
} from '@sbv-clients/ui-shared';
import { formatDateTimeString, nameOfFactory } from '@sbv-clients/util-misc';

import {
  CallOffAbsenceType,
  useCallOffsReportGet,
} from '../../../../../services/dashboard';

interface TableProps {
  callOffResponse: ReturnType<typeof useCallOffsReportGet>;
}

const mapToRows = (data: CallOffAbsenceType[]) => {
  return data.map((item, index) => ({
    ...item,
    id: index,
  }));
};

const rowKey = nameOfFactory<CallOffAbsenceType>();

const CallOffsGrid = ({ callOffResponse }: TableProps) => {
  const theme = useTheme();
  const { isError, error } = callOffResponse;
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: rowKey('absenceDate'),
        headerName: 'Day',
        flex: 1,
        renderCell: (params) => formatDateTimeString(params.row.absenceDate),
      },
      {
        field: rowKey('shiftTypeName'),
        headerName: 'Shift Type',
        flex: 1,
        renderCell: (params) =>
          `${params.row.positionName}/${params.row.shiftTypeName}`,
      },
      {
        field: rowKey('employeeName'),
        headerName: 'Employee',
        flex: 1,
      },
      {
        field: rowKey('absenceReason'),
        headerName: 'Reason',
        flex: 1,
      },
    ],
    []
  );

  const rows = useMemo(
    () => mapToRows(callOffResponse?.data?.absences || []),
    [callOffResponse?.data?.absences]
  );

  const noRowsOverlay = useNoRowsOverlay({
    isError,
    error,
    emptyTitle: 'No call offs',
  });

  return (
    <EmployeeProfileTableStyled
      name="call-offs-grid"
      headerTitle="Call offs"
      autoHeight={false}
      cardSx={{
        ...fillCardSx,
        boxShadow: 'none',
        border: `1px solid ${theme.custom?.listFooterLine}`,
        borderRadius: '12px',
      }}
      sx={{ flex: 1, minHeight: 0 }}
      hideFooter
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      loading={callOffResponse?.isLoading}
      slots={{ noRowsOverlay }}
    />
  );
};

export default CallOffsGrid;
